(() => {
  if (customElements.get("orlivy-complete-look")) return;

  class OrlivyCompleteLook extends HTMLElement {
    static states = new Map();

    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.mainFormId = this.dataset.mainForm;
      this.mainForm = document.getElementById(this.mainFormId);
      this.lookForm = this.querySelector("[data-look-form]");
      this.submitButton = this.querySelector("[data-look-submit]");
      this.status = this.querySelector("[data-look-status]");
      this.summary = this.querySelector("[data-look-summary]");
      this.total = this.querySelector("[data-look-total]");
      this.includes = this.querySelector("[data-look-includes]");
      this.cartLink = this.querySelector("[data-look-cart-link]");
      if (!(this.mainForm instanceof HTMLFormElement) || !this.lookForm || !this.submitButton) return;

      try {
        const config = JSON.parse(this.querySelector("[data-look-config]").textContent);
        this.primary = config.primary;
        this.messages = config.messages;
        this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang, {
          style: "currency",
          currency: this.dataset.currency,
          currencyDisplay: "code"
        });
        const root = this.route(this.dataset.rootUrl || window.Shopify?.routes?.root || "/");
        this.rootUrl = `${root.pathname.replace(/\/+$/, "")}/`;
        this.cartUrl = this.route(this.dataset.cartUrl || `${this.rootUrl}cart`).href;
        const addUrl = this.route(this.dataset.cartAddUrl || `${this.rootUrl}cart/add.js`);
        if (!addUrl.pathname.endsWith(".js")) addUrl.pathname += ".js";
        this.addUrl = addUrl.href;
        this.rows = Array.from(this.querySelectorAll("[data-look-choice]"), (element) => this.readRow(element));
      } catch {
        return;
      }

      const key = `${this.mainFormId}:${this.dataset.productId}`;
      if (!OrlivyCompleteLook.states.has(key)) {
        OrlivyCompleteLook.states.set(key, {
          choices: new Map(), instances: new Set(), busy: false, locked: false, notice: null
        });
      }
      this.state = OrlivyCompleteLook.states.get(key);
      this.state.instances.add(this);
      this.primaryPending = false;
      this.rows.forEach((row) => this.restoreRow(row));
      this.saveChoices();

      const signal = this.listeners.signal;
      this.addEventListener("change", (event) => this.onChoiceChange(event), { signal });
      this.lookForm.addEventListener("submit", (event) => this.onSubmit(event), { signal });
      this.mainForm.addEventListener("variant:change", (event) => {
        this.primary = event.detail?.variant || null;
        this.primaryPending = false;
        this.clearNotice();
        this.render();
      }, { signal });
      document.addEventListener("input", (event) => this.onPrimaryChange(event), { signal, capture: true });
      document.addEventListener("change", (event) => this.onPrimaryChange(event), { signal, capture: true });
      this.setAttribute("data-ready", "");
      this.render();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.state?.instances.delete(this);
    }

    route(value) {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error("Invalid cart origin");
      return url;
    }

    readRow(element) {
      const checkbox = element.querySelector("[data-look-include]");
      const image = element.querySelector("[data-look-image]");
      const price = element.querySelector("[data-look-price]");
      const links = Array.from(new Set([
        ...element.querySelectorAll("[data-look-product-link]"), image?.closest("a")
      ].filter(Boolean)));
      const variants = JSON.parse(element.querySelector("[data-look-variants]").textContent);
      if (!checkbox || !Array.isArray(variants)) throw new Error("Invalid companion");
      return {
        element, checkbox, image, price, links,
        key: element.dataset.productId,
        options: element.querySelector("[data-look-options]"),
        selects: Array.from(element.querySelectorAll("[data-look-option]")),
        variants: variants.filter((variant) => /^\d+$/.test(String(variant.id)) &&
          Number.isFinite(variant.price) && variant.price >= 0 && Array.isArray(variant.options)),
        originalPrice: price?.textContent || "",
        originalLinks: links.map((link) => link.getAttribute("href")),
        originalImage: image ? Object.fromEntries(["src", "srcset", "sizes", "alt"].map((name) => [name, image.getAttribute(name)])) : null
      };
    }

    restoreRow(row) {
      const saved = this.state.choices.get(row.key);
      row.checkbox.checked = saved?.checked === true;
      row.selects.forEach((select) => {
        const values = Array.from(select.options, (option) => option.value).filter(Boolean);
        const value = saved?.options[select.dataset.optionIndex];
        select.value = value && values.includes(value) ? value : values.length === 1 ? values[0] : "";
      });
    }

    saveChoices() {
      this.rows.forEach((row) => this.state.choices.set(row.key, {
        checked: row.checkbox.checked,
        options: Object.fromEntries(row.selects.map((select) => [select.dataset.optionIndex, select.value]))
      }));
    }

    message(key, replacements = {}) {
      let text = this.messages[key] || this.messages.choose || "";
      Object.entries(replacements).forEach(([name, value]) => {
        text = text.replaceAll(`__${name}__`, String(value));
      });
      return text;
    }

    clearNotice() {
      if (!this.state.locked && !this.state.busy) this.state.notice = null;
    }

    onChoiceChange(event) {
      if (!event.target.matches("[data-look-include], [data-look-option]")) return;
      if (this.state.busy || this.state.locked) return;
      this.clearNotice();
      this.saveChoices();
      this.render();
    }

    onPrimaryChange(event) {
      const control = event.target;
      if (!(control instanceof HTMLElement) || control.form !== this.mainForm) return;
      if (control.hasAttribute("data-option-position")) {
        this.primaryPending = true;
      } else if (!["id", "quantity", "selling_plan"].includes(control.name)) {
        return;
      }
      this.clearNotice();
      this.render();
    }

    mainSelection() {
      const form = document.getElementById(this.mainFormId);
      if (!(form instanceof HTMLFormElement)) return { valid: false, reason: "selection_changed" };
      const data = new FormData(form);
      const id = String(data.get("id") || "");
      const quantity = Number(data.has("quantity") ? data.get("quantity") : 1);
      const price = Number(this.primary?.price);
      if (this.primaryPending || id !== String(this.primary?.id) || !/^\d+$/.test(id)) {
        return { valid: false, reason: "selection_changed", data, form, quantity };
      }
      if (!this.primary?.available || !Number.isFinite(price) || price < 0) {
        return { valid: false, reason: "primary_unavailable", data, form, quantity };
      }
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        return { valid: false, reason: "choose", data, form, quantity };
      }
      return { valid: true, data, form, id, quantity, price };
    }

    rowVariant(row) {
      if (row.selects.some((select) => !select.value)) return { reason: "choose" };
      const matches = row.selects.length ? row.variants.filter((variant) => row.selects.every((select) =>
        variant.options[Number(select.dataset.optionIndex)] === select.value
      )) : row.variants;
      if (matches.length !== 1) return { reason: "unavailable" };
      return { variant: matches[0], reason: matches[0].available ? "" : "unavailable" };
    }

    renderRow(row) {
      const included = row.checkbox.checked;
      const choice = included ? this.rowVariant(row) : {};
      const variant = choice.variant;
      row.checkbox.disabled = this.state.busy || this.state.locked ||
        (!included && !row.variants.some((item) => item.available));
      row.checkbox.setAttribute("aria-expanded", String(included));
      if (row.options) row.options.hidden = !included;
      row.selects.forEach((select) => {
        select.disabled = !included || this.state.busy || this.state.locked;
        if (included && choice.reason === "unavailable") select.setAttribute("aria-invalid", "true");
        else select.removeAttribute("aria-invalid");
      });
      if (row.price) row.price.textContent = variant ? variant.priceText || this.money.format(variant.price / 100) : row.originalPrice;
      row.links.forEach((link, index) => {
        if (variant) {
          const url = new URL(row.originalLinks[index], window.location.origin);
          url.searchParams.set("variant", String(variant.id));
          link.href = url.href;
        } else {
          link.setAttribute("href", row.originalLinks[index]);
        }
      });
      if (row.image) {
        if (variant?.image?.src) {
          row.image.removeAttribute("srcset");
          row.image.removeAttribute("sizes");
          if (row.image.getAttribute("src") !== variant.image.src) row.image.setAttribute("src", variant.image.src);
          row.image.alt = variant.image.alt || row.originalImage.alt || "";
        } else {
          Object.entries(row.originalImage).forEach(([name, value]) => {
            if (value === null) row.image.removeAttribute(name);
            else if (row.image.getAttribute(name) !== value) row.image.setAttribute(name, value);
          });
        }
      }
      return { row, ...choice };
    }

    render() {
      if (!this.isConnected || !this.state) return;
      const selected = this.rows.map((row) => this.renderRow(row)).filter((choice) => choice.row.checkbox.checked);
      const main = this.mainSelection();
      const invalid = selected.find((choice) => choice.reason);
      const ready = main.valid && selected.length > 0 && !invalid;
      const quantity = Number.isSafeInteger(main.quantity) && main.quantity > 0 ? main.quantity : 0;
      if (this.summary) this.summary.hidden = selected.length === 0;
      if (this.includes) this.includes.textContent = this.message("includes", { QTY: quantity, EXTRAS: selected.length });
      if (this.total) this.total.textContent = ready ? this.money.format((main.price * quantity +
        selected.reduce((sum, choice) => sum + choice.variant.price, 0)) / 100) : "—";
      this.submitButton.hidden = selected.length === 0;
      this.submitButton.disabled = !ready || this.state.busy || this.state.locked;
      this.submitButton.textContent = this.state.busy ? this.message("adding") : this.message("add", { COUNT: quantity + selected.length });
      this.submitButton.setAttribute("aria-busy", String(this.state.busy));
      this.lookForm.setAttribute("aria-busy", String(this.state.busy));
      if (this.cartLink) {
        this.cartLink.href = this.cartUrl;
        this.cartLink.hidden = !this.state.locked;
      }
      const reason = selected.length ? !main.valid ? main.reason : invalid?.reason : "";
      const text = this.state.notice?.text || (reason ? this.message(reason) : "");
      if (this.status && this.status.textContent !== text) this.status.textContent = text;
    }

    publish() {
      this.state.instances.forEach((instance) => instance.render());
    }

    fail(message, refresh = false) {
      this.state.locked = true;
      this.state.notice = { text: [message || this.message("error"), this.message("unknown")].filter(Boolean).join(" ") };
      this.publish();
      if (refresh) document.dispatchEvent(new CustomEvent("cart:refresh"));
    }

    async request(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 30000);
      let response;
      try {
        response = await fetch(url, {
          credentials: "same-origin", ...options, signal: controller.signal
        });
        const data = await response.json();
        return { response, data };
      } catch (error) {
        error.confirmed = response?.ok === true;
        error.responseStatus = response?.status;
        throw error;
      } finally {
        clearTimeout(timer);
      }
    }

    goToCart() {
      this.state.locked = true;
      this.state.notice = { text: this.message("success") };
      this.publish();
      window.location.assign(this.cartUrl);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.state.busy || this.state.locked) return;
      const main = this.mainSelection();
      const selected = this.rows.filter((row) => row.checkbox.checked).map((row) => ({ row, ...this.rowVariant(row) }));
      if (!main.valid || !selected.length || selected.some((choice) => choice.reason)) {
        this.render();
        main.form?.reportValidity();
        this.lookForm.reportValidity();
        return;
      }
      if (!main.form.checkValidity()) {
        main.form.reportValidity();
        return;
      }
      if (Array.from(main.form.elements).some((control) => control.type === "submit" && control.getAttribute("aria-busy") === "true")) return;

      const sections = ["variant-added"];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true, detail: { sections }
      }));
      const sectionIds = Array.from(new Set(sections.filter((id) => typeof id === "string" && id))).slice(0, 5);
      const body = new FormData();
      body.set("items[0][id]", main.id);
      body.set("items[0][quantity]", String(main.quantity));
      for (const [name, value] of main.data.entries()) {
        const property = name.match(/^properties\[(.+)\]$/);
        if (property) body.append(`items[0][properties][${property[1]}]`, value);
        if (name === "selling_plan" && value) body.set("items[0][selling_plan]", value);
      }
      selected.forEach((choice, index) => {
        body.set(`items[${index + 1}][id]`, String(choice.variant.id));
        body.set(`items[${index + 1}][quantity]`, "1");
      });
      body.set("sections", sectionIds.join(","));
      body.set("sections_url", `${this.rootUrl}variants/${main.id}`);
      this.state.busy = true;
      this.state.notice = null;
      this.publish();
      let added = false;

      try {
        const result = await this.request(this.addUrl, {
          method: "POST", body, headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" }
        });
        added = result.response.ok;
        if (!added) {
          const description = typeof result.data?.description === "string" ? result.data.description : this.message("error");
          this.fail(description, true);
          return;
        }
        if (window.themeVariables?.settings?.cartType === "page" || window.themeVariables?.settings?.pageType === "cart") {
          this.goToCart();
          return;
        }
        const refreshed = await this.request(`${this.rootUrl}cart.js`, { cache: "no-store", headers: { Accept: "application/json" } });
        const cart = refreshed.data;
        const returnedSections = result.data?.sections;
        const items = Array.isArray(result.data?.items) ? result.data.items : result.data?.id ? [result.data] : [];
        if (!refreshed.response.ok || !Array.isArray(cart?.items) || !items.length ||
          sectionIds.some((id) => typeof returnedSections?.[id] !== "string" || !returnedSections[id].trim())) {
          this.goToCart();
          return;
        }
        cart.sections = returnedSections;
        this.state.notice = { text: this.message("success") };
        const source = Array.from(this.state.instances).find((instance) => instance.isConnected) || document.documentElement;
        source.dispatchEvent(new CustomEvent("variant:add", { bubbles: true, detail: { items, cart } }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true, detail: { baseEvent: "variant:add", cart }
        }));
      } catch (error) {
        if (added || error.confirmed) this.goToCart();
        else this.fail(this.message("error"), Boolean(error.responseStatus));
      } finally {
        this.state.busy = false;
        this.publish();
      }
    }
  }

  customElements.define("orlivy-complete-look", OrlivyCompleteLook);
})();
